import { useEffect, useRef, useState } from 'react';
import { SUPPORT_CHAT_UNAVAILABLE_EVENT } from '@/utils/supportChat';

const AUTO_HIDE_MS = 7000;

/**
 * A small, non-blocking notice shown when the support chat widget fails to
 * open (e.g. the remote widget script is still loading or was blocked).
 *
 * It exists because every "Start the chat" button on the site relies on that
 * third-party widget being present. Without this notice a click would appear to
 * do nothing and the visitor would simply leave. It never uses a browser alert
 * and quietly hides itself after a few seconds.
 */
export default function ChatSupportNotice() {
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handleUnavailable = () => {
      setVisible(true);

      if (hideTimer.current) {
        window.clearTimeout(hideTimer.current);
      }
      hideTimer.current = window.setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    };

    window.addEventListener(SUPPORT_CHAT_UNAVAILABLE_EVENT, handleUnavailable);

    return () => {
      window.removeEventListener(SUPPORT_CHAT_UNAVAILABLE_EVENT, handleUnavailable);
      if (hideTimer.current) {
        window.clearTimeout(hideTimer.current);
      }
    };
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed z-50 inset-x-4 bottom-24 sm:inset-x-auto sm:right-6 sm:bottom-24 sm:max-w-sm rounded-lg border border-background-200 bg-background-50 p-4 pr-11"
    >
      <span className="flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-800">
          <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
        </span>
        <span className="flex flex-col">
          <span className="font-heading text-sm font-semibold text-foreground-950">Chat is still loading</span>
          <span className="mt-1 text-xs leading-relaxed text-foreground-600">
            Tap the support bubble in the bottom-right corner to start the conversation — the free GoldWarden
            EA is handed out there.
          </span>
        </span>
      </span>

      <button
        type="button"
        onClick={() => setVisible(false)}
        aria-label="Dismiss notice"
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer"
      >
        <i className="ri-close-line text-lg" aria-hidden="true"></i>
      </button>
    </div>
  );
}